export type TenantBrand = 'gpdone' | 'wienergy' | 'avantag';

export interface UserTenant {
  identifier: string;
  name: string;
  uiDomain: string;
  userRole?: string;
}

// localhost has no tenant subdomains, so a switch there is remembered instead of navigated to.
const LOCAL_BRAND_KEY = 'localTenantBrand';
const SWITCH_PARAM = 'tenantSwitch';

export function brandFromHost(host: string): TenantBrand {
  const h = (host ?? '').toLowerCase();
  if (h.includes('avantag')) return 'avantag';
  if (h.includes('wienergy') || h.includes('wi-energy')) return 'wienergy';
  return 'gpdone';
}

function isLocalHost(): boolean {
  const host = window.location?.hostname ?? '';
  return host === 'localhost' || host === '127.0.0.1';
}

function toUrl(uiDomain: string): URL | null {
  if (!uiDomain) return null;
  try {
    return new URL(/^https?:\/\//i.test(uiDomain) ? uiDomain : `https://${uiDomain}`);
  } catch {
    return null;
  }
}

export function currentBrand(): TenantBrand {
  if (typeof window === 'undefined') return 'gpdone';
  if (isLocalHost()) {
    try {
      const override = localStorage.getItem(LOCAL_BRAND_KEY);
      if (override) return brandFromHost(override);
    } catch {
      // storage unavailable: fall back to the host
    }
  }
  return brandFromHost(window.location?.hostname ?? '');
}

export function canSwitchTo(tenant: UserTenant): boolean {
  return toUrl(tenant.uiDomain) !== null;
}

export function switchTenant(tenant: UserTenant): void {
  const url = toUrl(tenant.uiDomain);
  if (!url) return;

  if (isLocalHost()) {
    try {
      localStorage.setItem(LOCAL_BRAND_KEY, url.hostname);
    } catch {
      return;
    }
    window.location.assign(window.location.origin + (document.querySelector('base')?.getAttribute('href') ?? '/'));
    return;
  }

  // Keep the locale prefix (/en/) so the user lands in the same language.
  const target = new URL(document.querySelector('base')?.getAttribute('href') ?? '/', url.origin);
  // A flag only, never the email: the query string reaches the target server's access log.
  target.searchParams.set(SWITCH_PARAM, '1');
  window.location.assign(target.toString());
}

// Each tenant is its own origin with its own MSAL cache; a user arriving from a switch is signed
// in straight away instead of being shown the login page they already passed.
export function takeTenantSwitchFlag(): boolean {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SWITCH_PARAM)) return false;
  url.searchParams.delete(SWITCH_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
  return true;
}
